import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth, useDialog, useOneSignalCustomState, useSideMenu } from '../state';
import shallow from 'zustand/shallow';
import { useInvalidateWallet } from '@/features/wallet/hooks/use-invalidate-wallet';
import { useLogout } from '../services';
import { useCheckClientCity } from './use-check-client-city';
import { useUpdatePath } from './use-update-path';

const LOGOUT_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds

export const useLogoutTimer = () => {
  const setDialog = useDialog(s => s.setDialog, shallow);
  const [isPromptVisible, setIsPromptVisible] = useState<boolean>(false);
  const [remainingTime, setRemainingTime] = useState<number>(LOGOUT_TIMEOUT / 1000); // 15 minutes in seconds
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const setSideMenu = useSideMenu(s => s.setSideMenu, shallow);
  const [user, setUser] = useAuth(s => [s.user, s.setUser], shallow);
  const { checkGuestCity } = useCheckClientCity();
  const { updateLogoutPath } = useUpdatePath();

  const logout = useLogout();
  const { invalidateAllWallet } = useInvalidateWallet();
  const [deviceId] = useOneSignalCustomState(s => [s.muviDeviceId], shallow);

  const handleLogoutConfirm = useCallback(() => {
    logout.safeMutateAsync(
      {
        headers: {
          'X-Device-Id': deviceId,
        },
      },
      {
        onSuccess: () => {
          setUser(null);

          checkGuestCity();
          setSideMenu(false);
          updateLogoutPath();
          setTimeout(() => {
            invalidateAllWallet();
            setDialog({
              wrapper: true,
              isLogoutTimerDialog: true,
            });
          }, 1000);
          setDialog({
            wrapper: true,
            isLogoutTimerDialog: true,
          });
        },
      }
    );
  }, [
    checkGuestCity,
    deviceId,
    invalidateAllWallet,
    logout,
    setDialog,
    setSideMenu,
    setUser,
    updateLogoutPath,
  ]);

  const handleLogout = (): void => {
    setDialog({
      wrapper: true,
      isLogoutTimerDialog: true,
    });
    console.log('Logging out due to inactivity');
    handleLogoutConfirm();
  };

  // const startCountdown = (): void => {
  //   intervalIdRef.current = setInterval(() => {
  //     setRemainingTime(prev => {
  //       if (prev <= 1) {
  //         clearInterval(intervalIdRef.current!);
  //         handleLogout();
  //         return 0;
  //       }
  //       return prev - 1;
  //     });
  //   }, 1000);
  // };

  const resetTimer = (): void => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }
    setIsPromptVisible(false);
    setRemainingTime(LOGOUT_TIMEOUT / 1000);
    timeoutIdRef.current = setTimeout(() => {
      // setIsPromptVisible(true);
      // startCountdown();
      handleLogout();
    }, LOGOUT_TIMEOUT);
  };

  const handleActivity = (): void => {
    resetTimer();
  };

  useEffect(() => {
    if (!user?.token) return;

    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keypress', handleActivity);

    resetTimer();

    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keypress', handleActivity);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { remainingTime, handleLogout, isPromptVisible };
};
